import { FC, useRef } from "react";
import {
  AccordionHeader,
  AccordionHeaderText,
  ChipOptionsDataType,
  MuiDoubleLabel,
  MuiGrid,
  MuiViewMorePopOver,
} from "../../../../components";
import { MuiDoubleLabelWithChip } from "../../../../components/doubleLabelWithChip/MuiDoubleLabelWithChip";
import { viewMorePopOverGridSize } from "../../../../helpers/constants";
import { getValueOrBlank, isNotEmpty } from "../../../../helpers/utils";
import { useGridContainer } from "../../../../hooks/useGridContainer";
import { useNavigationSiteCore } from "../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../hooks/useSiteCore";
import { sortDaysByWeekOrder } from "../../../createUser/helper";
import { ViewUserDataType } from "../../../createUser/modelsAndSchemas/models";
import { createUserSiteCoreKeys } from "../../../createUser/siteCore/SiteCoreKeys";
import { ViewUserCoachDetailWrapper } from "./ViewUserCoachDetails.style";

interface ViewUserCoachDetailProps {
  userData: ViewUserDataType;
}

export const ViewUserCoachDetail: FC<ViewUserCoachDetailProps> = ({
  userData,
}) => {
  const { useFetchLabel, commonLabels } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const { gridSizeMedium: gridSize, gridSizeMediumClass } =
    useGridContainer(true);
  const gridRef = useRef<HTMLDivElement>(null);

  const renderHeader = (label: string) => {
    return <AccordionHeaderText label={label} />;
  };

  const getCoachDetailLabel = useFetchLabel(
    createUserSiteCoreKeys.coachDetailsKey,
  );
  const weeklyPeriods = userData?.schedulingAvailability?.weekly_periods;

  const startTime =
    weeklyPeriods?.length &&
    weeklyPeriods.every((p) => p.start_time === weeklyPeriods[0].start_time)
      ? weeklyPeriods[0].start_time
      : "";

  const endTime =
    weeklyPeriods?.length &&
    weeklyPeriods.every((p) => p.end_time === weeklyPeriods[0].end_time)
      ? weeklyPeriods[0].end_time
      : "";

  const getSortedWeeklyPeriodOptions = (
    weeklyPeriods: { day?: string }[] = [],
  ): ChipOptionsDataType[] => {
    const mapped = weeklyPeriods.map((item) => ({
      label: item?.day
        ? item.day.charAt(0).toUpperCase() + item.day.slice(1).toLowerCase()
        : "",
      value: item?.day ?? "",
    }));

    const sorted = sortDaysByWeekOrder(mapped.map((item) => item.value));

    return sorted.map((day) => ({
      label: day.charAt(0).toUpperCase() + day.slice(1).toLowerCase(),
      value: day,
    }));
  };

  return (
    <ViewUserCoachDetailWrapper>
      <AccordionHeader
        className="accordion-header"
        title={renderHeader(
          getCoachDetailLabel(createUserSiteCoreKeys.profileSectionTitle),
        )}
        isExpanded={true}
        showExpandCollapseIcon={false}
      >
        <MuiGrid container className="accordion-container">
          <MuiGrid
            size={viewMorePopOverGridSize}
            className={gridSizeMediumClass}
          >
            <MuiDoubleLabel
              label={getCoachDetailLabel(createUserSiteCoreKeys.bioData)}
              replaceEmptyWithDash
              value={
                isNotEmpty(userData?.bio) ? (
                  <MuiViewMorePopOver
                    anchorText={commonLabels.viewMore}
                    message={userData?.bio?.trim()}
                    placement="bottom-end"
                  />
                ) : (
                  ""
                )
              }
            />
          </MuiGrid>
        </MuiGrid>
        <MuiGrid container className="accordion-container">
          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <MuiDoubleLabel
              label={getCoachDetailLabel(createUserSiteCoreKeys.expertise)}
              replaceEmptyWithDash
              value={userData?.expertise?.trim()}
              isValueWithEllipsis
            />
          </MuiGrid>
          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <MuiDoubleLabel
              label={getCoachDetailLabel(createUserSiteCoreKeys.linkedinId)}
              replaceEmptyWithDash
              value={userData?.socialIdDetails}
              isValueWithEllipsis
            />
          </MuiGrid>
        </MuiGrid>
        <MuiGrid
          container
          className="accordion-container coach-details-container"
        >
          <MuiGrid
            size={viewMorePopOverGridSize}
            className={gridSizeMediumClass}
          >
            <MuiDoubleLabel
              label={getCoachDetailLabel(
                createUserSiteCoreKeys.coachingLanguages,
              )}
              replaceEmptyWithDash
              value={
                isNotEmpty(userData?.language) ? (
                  <MuiViewMorePopOver
                    anchorText={commonLabels.viewMore}
                    message={userData?.language?.trim()}
                    placement="bottom-end"
                  />
                ) : (
                  ""
                )
              }
            />
          </MuiGrid>
        </MuiGrid>
      </AccordionHeader>
      <AccordionHeader
        className="accordion-header"
        title={renderHeader(
          getCoachDetailLabel(createUserSiteCoreKeys.cronofySectionTitle),
        )}
        isExpanded={true}
        showExpandCollapseIcon={false}
      >
        <MuiGrid
          container
          className="accordion-container coach-details-container"
        >
          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <MuiDoubleLabel
              label={getCoachDetailLabel(createUserSiteCoreKeys.coachTimeZone)}
              replaceEmptyWithDash
              value={getValueOrBlank(userData?.ianaTimeZoneIdentifierName)}
              isValueWithEllipsis
            />
          </MuiGrid>
          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <MuiDoubleLabel
              label={getCoachDetailLabel(
                createUserSiteCoreKeys.serviceAccountName,
              )}
              replaceEmptyWithDash
              value={getValueOrBlank(userData?.serviceAccountName)}
              isValueWithEllipsis
            />
          </MuiGrid>
          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <MuiDoubleLabelWithChip
              className="chip-day"
              label={getCoachDetailLabel(createUserSiteCoreKeys.availableDays)}
              replaceEmptyWithDash
              value={getSortedWeeklyPeriodOptions(
                userData?.schedulingAvailability?.weekly_periods,
              )}
              chipPopoverGridRef={gridRef}
              chipPopoverHeaderLabel={getCoachDetailLabel(
                createUserSiteCoreKeys.selectedAvailableDays,
              )}
              isValueWithEllipsis
              showMoreARIALabel={commonARIALabel.showNMore}
              removeChipNameARIALabel={commonARIALabel.removeChipName}
              closePopoverARIALabel={commonARIALabel.closePopover}
            />
          </MuiGrid>
          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <MuiDoubleLabel
              label={getCoachDetailLabel(
                createUserSiteCoreKeys.workingHoursStartTime,
              )}
              replaceEmptyWithDash
              value={startTime}
              isValueWithEllipsis
            />
          </MuiGrid>
          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <MuiDoubleLabel
              label={getCoachDetailLabel(
                createUserSiteCoreKeys.workingHoursEndTime,
              )}
              replaceEmptyWithDash
              value={endTime}
              isValueWithEllipsis
            />
          </MuiGrid>
        </MuiGrid>
      </AccordionHeader>
    </ViewUserCoachDetailWrapper>
  );
};
